import { api } from '../api'
import { useCachedFetch } from '../lib/swr'
import { useTheme } from '../theme'
import { avecAlpha } from '../lib/couleurs'

// Socle commun des DEUX explorers (S52) : carte de lieu, identite visuelle,
// facettes, chargement des lieux pilotes au BO. Les parcours (client :
// grille → liste d'evenements ; producteur : grille → modules producteur)
// vivent dans PageExplorer / PageExplorerProducteur.

// Lieux de l'Explorer — pilotes au BO via GET /public/modules (etat ACTIF/BIENTOT).
// On reutilise les modules existants : MARCHE = lieu « Évènements » (ADR-062),
// TOURNEE = « Paniers » (tournees). Fallback si l'API echoue.
export const FALLBACK_MODULES = [
  { code: 'MARCHE', nom: 'Évènements', emoji: '🎪', description: 'Les marchés, festivals et villages près de chez vous', state: 'ACTIF' },
  { code: 'TOURNEE', nom: 'Paniers', emoji: '🧺', description: 'Vos paniers précomposés livrés en tournée', state: 'BIENTOT' },
]

// Identite visuelle par lieu (couleur centralisee — DESIGN-SYSTEM.md §3 MODULE_THEME).
// L'API /public/modules pilote nom/emoji/etat (BO) ; la couleur vit ici.
const MODULE_COLOR = {
  MARCHE: 'rgb(var(--c-teal))', TOURNEE: 'rgb(var(--c-primary))', JADEN: '#5e9e4e',
  LAVWA: 'rgb(var(--c-indigo))', RASIN: '#b06a43', TRANSFO: '#d99a2b',
}
export const moduleColor = (code) => MODULE_COLOR[code] || 'rgb(var(--c-green))'

// Facettes (capacites) d'un lieu : quels profils peuvent y agir. Sert a afficher
// les « badges facettes » = les casquettes de l'utilisateur pertinentes pour ce lieu.
export const MODULE_FACETTES = {
  MARCHE: ['client', 'producteur'],
  TOURNEE: ['client', 'producteur'],
}
// Pastille de casquette (lentille) — couleur + emoji par role (DESIGN-SYSTEM.md §2).
export const LENS = {
  client: { e: '🧑🏾', c: 'rgb(var(--c-primary))', l: 'Client' },
  producteur: { e: '🌱', c: 'rgb(var(--c-teal))', l: 'Producteur' },
}

// Un lieu est ouvrable si ACTIF, ou si le compte a un acces anticipe
// (User.enabledModules — surcouche au-dessus de l'etat global). Alias legacy :
// 'TOURNEES'/'TOURNEE' dans enabledModules ouvrent le lieu « Paniers » (TOURNEE).
export function isEnabled(m, user) {
  const allowed = new Set(user?.enabledModules || [])
  if (m.state === 'ACTIF') return true
  if (allowed.has(m.code)) return true
  if (m.code === 'TOURNEE' && (allowed.has('TOURNEES') || allowed.has('TOURNEE'))) return true
  return false
}

/**
 * Lieux pilotes au BO (GET /public/modules) — cache SWR : instantane au retour,
 * pas de flip-flop « Bientot » -> « Actif ». null = tout premier chargement
 * (afficher un skeleton, jamais le fallback qui basculerait juste apres).
 */
export function useModulesPortail() {
  const { data, loading } = useCachedFetch('/public/modules', () => api.getModules(), { persist: true })
  return Array.isArray(data) && data.length > 0 ? data : (loading ? null : FALLBACK_MODULES)
}

export function SkeletonGrille() {
  return (
    <div className="grid grid-cols-2 gap-3 auto-rows-fr">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className={`h-[124px] rounded-2xl animate-pulse bg-ink/5`} />
      ))}
    </div>
  )
}

export default function ModuleCard({ module: m, enabled, onClick, animationDelay, facetteRoles = [] }) {
  const { isWeb3 } = useTheme()
  const tc = 'text-ink'
  const ts = 'text-ink/60'
  const cardBg = isWeb3 ? 'bg-white/5 border-white/10' : 'glass-surface'
  const c = moduleColor(m.code)

  const disabled = !enabled
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{ animationDelay }}
      className={`relative h-full flex flex-col rounded-2xl border p-4 text-left transition-transform animate-fade-up ${cardBg} ${
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer active:scale-[0.97]'
      }`}
    >
      <div
        className="w-14 h-14 rounded-[18px] flex items-center justify-center text-[27px] mb-2.5"
        style={{
          background: isWeb3 ? avecAlpha(c, 0.13) : `linear-gradient(135deg, ${c}, ${avecAlpha(c, 0.67)})`,
          boxShadow: isWeb3 ? 'none' : `0 10px 22px ${avecAlpha(c, 0.25)}`,
        }}
      >
        {m.emoji}
      </div>
      <p className={`text-base font-semibold ${tc}`}>{m.nom}</p>
      <p className={`text-xs mt-0.5 ${ts}`}>{m.description}</p>
      {/* Badges facettes : les casquettes de l'utilisateur qui peuvent agir dans ce lieu */}
      {facetteRoles.length > 0 && (
        <div className="flex gap-1.5 mt-2">
          {facetteRoles.map((r) => (
            <span
              key={r}
              title={LENS[r]?.l}
              className="w-[22px] h-[22px] rounded-full flex items-center justify-center text-[11px]"
              style={{ background: `${LENS[r]?.c}2b`, border: `1px solid ${LENS[r]?.c}66` }}
            >
              {LENS[r]?.e}
            </span>
          ))}
        </div>
      )}
      {disabled && (
        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-ink/10 text-ink">
          Bientôt
        </span>
      )}
    </button>
  )
}
